import Typography from '@mui/material/Typography';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { DialogVariantOptions } from 'shared/assets/styles';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';

import * as Styled from './choice.style';

export type ChoiceDialogProps = {
  open: boolean;
  // opt-in for the caller that raises this from inside a PopupMenu
  abovePopups?: boolean;
  title: string;
  message: string;
  // rendered rightmost, where the eye lands last
  primaryLabel: string;
  secondaryLabel: string;
  cancelLabel?: string;
  // red suits a choice that throws something away; a neutral one would read as a warning
  actionColor?: Colors;
  onPrimary: () => void;
  onSecondary: () => void;
  onCancel: () => void;
};

// The three-way "this one / that one / never mind" prompt; ConfirmDialog is yes/no only
export const ChoiceDialog: React.FC<ChoiceDialogProps> = ({
  open,
  title,
  message,
  primaryLabel,
  secondaryLabel,
  cancelLabel,
  actionColor = Colors.Error,
  onPrimary,
  onSecondary,
  onCancel,
  abovePopups,
}) => {
  const { t } = useTranslation();

  return (
    <Styled.Dialog
      $abovePopups={abovePopups}
      open={open}
      onClose={onCancel}
      variant={DialogVariantOptions.ExtraSmall}
    >
      <Styled.Header>
        <Typography variant="h5" component="h1">
          {title}
        </Typography>
      </Styled.Header>

      <Styled.Body>
        <Typography variant="body2">{message}</Typography>
      </Styled.Body>

      <Styled.Footer>
        <Button
          variant={Variants.Contained}
          size={Sizes.Medium}
          color={Colors.White}
          onClick={onCancel}
        >
          {cancelLabel ?? t('general.cancel')}
        </Button>

        <Styled.Interactions>
          <Button
            variant={Variants.Contained}
            size={Sizes.Medium}
            color={actionColor}
            onClick={onSecondary}
          >
            {secondaryLabel}
          </Button>
          <Button
            variant={Variants.Contained}
            size={Sizes.Medium}
            color={actionColor}
            onClick={onPrimary}
          >
            {primaryLabel}
          </Button>
        </Styled.Interactions>
      </Styled.Footer>
    </Styled.Dialog>
  );
};
